import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface TabDef<T extends string> {
  key: T;
  label: ReactNode;
}

export interface TabsProps<T extends string> {
  tabs: ReadonlyArray<TabDef<T>>;
  current: T;
  onChange: (key: T) => void;
  className?: string;
}

/**
 * Onglets avec indicateur `accent` animé au bas (slide d'un onglet à l'autre).
 *
 * Implémentation : au lieu de stocker les refs DOM dans un objet indexé par
 * `t.key`, on résout l'élément actif via `querySelector` sur un attribut
 * `data-tab-key`. Plus robuste : pas de désync possible entre ordre de refs
 * et valeur de `current` lors de certains renders (décalage d'un cran
 * reproduit sur l'onglet Config).
 *
 * Re-mesure : au changement d'onglet, au resize fenêtre, et après chargement
 * des webfonts (Inter via `@fontsource` arrive en async, les largeurs de
 * labels changent une fois la police appliquée).
 */
export function Tabs<T extends string>({ tabs, current, onChange, className }: TabsProps<T>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const selector = `[data-tab-key="${CSS.escape(String(current))}"]`;
    const activeButton = container.querySelector<HTMLElement>(selector);
    if (!activeButton) return;
    const label = activeButton.querySelector<HTMLElement>('[data-tab-label]') ?? activeButton;
    // `offsetLeft` / `offsetWidth` : plus fiables que `getBoundingClientRect`
    // sous le zoom CSS global appliqué via `html { zoom: var(--app-zoom) }`.
    // Les rects viewport sont scalés par le zoom, ce qui décalait l'indicateur
    // progressivement vers la droite à mesure qu'on changeait d'onglet.
    // `offsetLeft` est dans l'espace CSS pre-zoom → cohérent avec les CSS props
    // qu'on pose (`left` + `width`), qui subissent le même zoom que le layout.
    const labelLeftInContainer = activeButton.offsetLeft + label.offsetLeft;
    // Bordure légèrement plus large que le texte : 12 px de débordement de
    // chaque côté pour un rendu aéré mais calé sur le label.
    const PAD = 12;
    setIndicator({
      left: labelLeftInContainer - PAD,
      width: label.offsetWidth + PAD * 2,
    });
  }, [current]);

  useLayoutEffect(() => {
    measure();
  }, [measure, tabs]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      void (document.fonts as { ready: Promise<unknown> }).ready.then(measure);
    }
    return () => window.removeEventListener('resize', onResize);
  }, [measure]);

  return (
    <div
      ref={containerRef}
      role="tablist"
      className={cn('relative mb-6 flex gap-1 border-b border-border-subtle', className)}
    >
      {tabs.map((t) => (
        <button
          key={t.key}
          data-tab-key={t.key}
          role="tab"
          aria-selected={current === t.key}
          onClick={() => onChange(t.key)}
          className={cn(
            'relative px-4 py-2 text-sm font-medium transition-colors',
            current === t.key ? 'text-brand' : 'text-text-muted hover:text-brand',
          )}
        >
          <span data-tab-label>{t.label}</span>
        </button>
      ))}
      {indicator && (
        <span
          aria-hidden
          className="absolute bottom-[-1px] h-[2px] rounded-full bg-accent transition-[left,width] duration-200 ease-out"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
    </div>
  );
}
